import React, { useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { useDebounce } from "use-debounce";

import {
  FloatingWindow,
  FloatingWindowProps,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { EntityDetailsTable } from "src/dataTable/EntityDetailsTable";
import { Pill } from "src/design-system/Pill";
import { EntityDetailsSidePane } from "src/entities/entityView/EntityDetailsSidePane";
import { LinkedEntityPill } from "src/entities/entityView/LinkedEntityPill";
import { EntityResourceProperties } from "src/entities/queries";
import { Environment } from "src/hooks/useEnvironment";

type BaseProps = {
  name: string;
  schemaId: string;
  entityIcon?: IconProp;
  fullWidth?: boolean;
  isPinnable?: boolean;
};

type EntityIdProps = BaseProps & {
  id: string;
  environment: Environment;
  entityData?: never;
};

type EntityDataProps = BaseProps & {
  id?: never;
  environment?: never;
  entityData: EntityResourceProperties;
};

type Props = EntityIdProps | EntityDataProps;

type EntityDetailsFloatingWindowProps = Props &
  Omit<FloatingWindowProps, "title" | "children">;

export const EntityDetailsFloatingWindow: React.FC<
  EntityDetailsFloatingWindowProps
> = ({
  schemaId,
  entityIcon,
  name,
  id,
  entityData,
  environment,
  ...floatingWindowProps
}) => {
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <FloatingWindow
        height={"h-auto" as `h-${number}`}
        maximizable={false}
        pinnable={false}
        placement="auto"
        title={
          <LinkedEntityPill
            entityIcon={entityIcon}
            id={(entityData?.properties.id as string) ?? id}
            name={name}
            schemaId={schemaId}
          />
        }
        draggable
        {...floatingWindowProps}
      >
        <div className="decideScrollbar mt-2 h-full max-h-[560px] overflow-hidden">
          {entityData ? (
            <EntityDetailsTable entityData={entityData} schemaId={schemaId} />
          ) : (
            <EntityDetailsSidePane
              entityId={id}
              environment={environment}
              schemaId={schemaId}
              showHeader={false}
            />
          )}
        </div>
      </FloatingWindow>
    </div>
  );
};

export const EntityDetailsWindowPill: React.FC<Props> = ({
  name,
  id,
  schemaId,
  entityIcon = faQuestionCircle,
  entityData,
  environment,
  isPinnable,
}) => {
  const [pillHovered, setPillHovered] = useDebounce<boolean>(false, 400);
  const [windowHovered, setWindowHovered] = useState<boolean>(false);

  const shouldWindowBeOpen = pillHovered || windowHovered;

  const floatingWindowProps = entityData
    ? { entityData, id: undefined, environment: undefined }
    : { id, entityData: undefined, environment };

  return (
    <EntityDetailsFloatingWindow
      button={
        <div
          className="cursor-default"
          onMouseLeave={() => {
            setTimeout(() => {
              setPillHovered(false);
            }, 100);
          }}
          onMouseOver={() => {
            setPillHovered(true);
          }}
        >
          <Pill hovered={shouldWindowBeOpen} size="sm" variant="gray" maxWidth>
            <Pill.Icon icon={entityIcon} />
            <Pill.Text>{name}</Pill.Text>
          </Pill>
        </div>
      }
      entityIcon={entityIcon}
      isOpen={shouldWindowBeOpen}
      name={name}
      pinnable={isPinnable}
      schemaId={schemaId}
      onMouseLeave={() => setWindowHovered(false)}
      onMouseOver={() => setWindowHovered(true)}
      {...floatingWindowProps}
    />
  );
};
